import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import {
  ColumnDef,
  getCoreRowModel,
  PaginationState,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import { HiSearch } from 'react-icons/hi';
import { skipToken } from '@reduxjs/toolkit/query';
import RowCell from '../../../../components/tables/rowCell';
import OnlineStatePill from '../../../../components/pill/onlineStatePill';
import DateTableLabel from '../../../../components/date/dateTableLabel';
import TableActionButton from '../../../../components/tables/tableActionButton';
import {
  enhancedCheckpointApi,
  useCheckpointServiceListCheckpointsForBuildingQuery
} from '../../../../api/enhanced/enhancedCheckpoints';
import { V1Alpha1Checkpoint } from '../../../../api/gen/checkpoints';
import DataTable from '../../../../components/tables/dataTable';
import TemperaturePill from '../../../../components/pill/temperaturePill';
import HumidityPill from '../../../../components/pill/humidityPill';
import { useBuildingServiceListAreasForBuildingQuery } from '../../../../api/enhanced/enhancedBuildings';

interface OverviewTableProps {
  groupId?: string;
  buildingId?: string;
  areaId?: string;
  searchTerm?: string;
  clickHandler: (checkpointId: string) => void;
}

function TempOverviewTable({
  groupId,
  buildingId,
  areaId,
  searchTerm,
  clickHandler
}: OverviewTableProps): ReactElement {
  const [sortColumns, setSortColumns] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20
  });

  useEffect(() => {
    let sortingColumns = 'checkpoint_number:ASC';
    if (sorting.length > 0) {
      sorting.forEach((sortCol) => {
        sortingColumns = `${sortCol.id}:${sortCol.desc ? 'DESC' : 'ASC'}`;
      });
    }
    setSortColumns(sortingColumns);
  }, [sorting, setSortColumns]);

  const {
    data: areaData,
    isLoading: areaIsLoading,
    isSuccess: areaIsSuccess,
    isError: areaIsError,
    error: areaError
  } = useBuildingServiceListAreasForBuildingQuery(
    buildingId != null && groupId != null
      ? { buildingId, groupId, limit: 200, page: 0 }
      : skipToken
  );

  useEffect(() => {
    enhancedCheckpointApi.util.invalidateTags(['SENSOR_TYPE_TEMP']);
    setPagination((state) => ({ ...state, pageIndex: 0 }));
    setSortColumns('checkpoint_number:ASC');
  }, [areaId, pageSize, searchTerm]);

  const columns = useMemo<ColumnDef<V1Alpha1Checkpoint>[]>(() => {
    const getAreaDescriptionById = (id: string): string => {
      if (areaIsError) {
        return 'Fehler beim Laden der Bereiche';
      }

      const area = areaData?.areas?.find(
        (currentArea) => currentArea.id === id
      );
      return area != null && area.description != null
        ? area.description
        : 'Keinem Bereich zugewiesen';
    };

    return [
      {
        header: 'Prüfpunkt',
        id: 'checkpoint_number',
        cell: (info) => RowCell({ title: info.getValue<string>() }),
        accessorFn: (row) => row.checkpointNumber,
        footer: (props) => props.column.id
      },
      {
        header: 'Beschreibung',
        id: 'description',
        accessorFn: (row) => row.description,
        cell: (info) => RowCell({ title: info.getValue<string>() }),
        footer: (props) => props.column.id
      },
      {
        header: 'Bereich',
        accessorFn: (row) => row.areaId,
        cell: (info) =>
          RowCell({
            title: getAreaDescriptionById(info.getValue<string>())
          }),
        footer: (props) => props.column.id,
        enableSorting: false
      },
      {
        header: 'Temperatur',
        id: 'temperature',
        accessorFn: (row) => row.temperaturePayload?.temperature,
        cell: (info) =>
          TemperaturePill({
            value: info.getValue<number>(),
            minLimit: info.row.original.temperaturePayload?.minLimit
              ? info.row.original.temperaturePayload?.minLimit
              : 0,
            maxLimit: info.row.original.temperaturePayload?.maxLimit
              ? info.row.original.temperaturePayload?.maxLimit
              : 0
          }),
        footer: (props) => props.column.id
      },
      {
        header: 'Luftfeuchtigkeit',
        id: 'humidity',
        accessorFn: (row) => row.temperaturePayload?.humidity,
        cell: (info) =>
          HumidityPill({
            value: info.getValue<number>(),
            minLimit: info.row.original.temperaturePayload?.humidityMinLimit
              ? info.row.original.temperaturePayload?.humidityMinLimit
              : 0,
            maxLimit: info.row.original.temperaturePayload?.humidityMaxLimit
              ? info.row.original.temperaturePayload?.humidityMaxLimit
              : 0
          }),
        footer: (props) => props.column.id
      },
      {
        header: 'Verfügbarkeit',
        id: 'is_online',
        accessorFn: (row) => row.loraMetadata?.isOnline,
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (info) => (
          <OnlineStatePill
            key={info.row.original.id}
            state={info.getValue<boolean>()}
            timestamp={info.row.original.loraMetadata?.lastSignal}
            checkpointId={info.row.original.id}
          />
        ),
        footer: (props) => props.column.id
      },
      {
        header: 'Letztes Signal',
        id: 'last_signal',
        cell: (info) =>
          DateTableLabel({
            timestamp: info.getValue<string>(),
            className: 'px-6'
          }),
        accessorFn: (row) => row.loraMetadata?.lastSignal,
        footer: (props) => props.column.id
      },
      {
        header: 'Batteriezustand',
        id: 'battery_voltage',
        cell: (info) => RowCell({ title: `${info.getValue<string>()} V` }),
        accessorFn: (row) => row.temperaturePayload?.batteryVoltage,
        footer: (props) => props.column.id
      },
      {
        header: 'Details',
        accessorFn: (row) => row.id,
        cell: (info) =>
          TableActionButton({
            icon: <HiSearch size={20} />,
            onClick: () => clickHandler(info.getValue<string>())
          }),
        footer: (props) => props.column.id,
        enableSorting: false
      }
    ];
  }, [areaIsError, areaData?.areas, clickHandler]);

  const { data, isLoading, isSuccess, isFetching, isError } =
    useCheckpointServiceListCheckpointsForBuildingQuery(
      buildingId != null && groupId != null && groupId !== ''
        ? {
            groupId,
            buildingId,
            areaId,
            limit: pageSize,
            page: pageIndex,
            sensorType: 'SENSOR_TYPE_TEMP',
            searchBy: searchTerm != null ? searchTerm : '',
            sortBy: sortColumns
          }
        : skipToken
    );

  const pagination = useMemo(
    () => ({ pageIndex, pageSize }),
    [pageSize, pageIndex]
  );

  const total = Number(data?.total);
  const limit = data?.limit;
  const pageCount =
    total != null && limit != null ? Math.ceil(total / limit) : -1;

  const table = useReactTable({
    data: data?.checkpoints ?? [],
    columns,
    pageCount,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    state: { pagination, sorting },
    onSortingChange: setSorting,
    manualPagination: true,
    manualSorting: true
  });

  return (
    <DataTable
      isLoading={isLoading || isFetching}
      isError={isError}
      isEmpty={
        isSuccess && data.checkpoints != null && data.checkpoints.length === 0
      }
      pageSize={pageSize}
      table={table}
    />
  );
}

export default TempOverviewTable;
